import { AnimatePresence, motion } from 'framer-motion';
import { Bookmark, Heart, MoreHorizontal, ThumbsDown, VolumeX, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/components/ui';
import { cn } from '@/lib/utils';

interface ActionBarProps {
  contentId: string;
  isLiked: boolean;
  isSaved: boolean;
  showMore?: boolean;
  onLike: () => void;
  onSave: () => void;
  onDismiss: () => void;
  onMore: (action: 'feedback' | 'muteAuthor' | 'muteSource') => void;
  className?: string;
}

export function ActionBar({
  isLiked,
  isSaved,
  showMore = true,
  onLike,
  onSave,
  onDismiss,
  onMore,
  className,
}: ActionBarProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuAction = (action: 'feedback' | 'muteAuthor' | 'muteSource') => {
    onMore(action);
    setMenuOpen(false);
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {/* Like */}
      <IconButton
        onClick={onLike}
        active={isLiked}
        aria-label={isLiked ? t('actions.liked') : t('actions.like')}
      >
        <Heart className={cn('w-5 h-5', isLiked && 'fill-current')} />
      </IconButton>

      {/* Save */}
      <IconButton
        onClick={onSave}
        active={isSaved}
        aria-label={isSaved ? t('actions.saved') : t('actions.save')}
      >
        <Bookmark className={cn('w-5 h-5', isSaved && 'fill-current')} />
      </IconButton>

      {/* Dismiss */}
      <IconButton onClick={onDismiss} aria-label={t('actions.dismiss')}>
        <X className="w-5 h-5" />
      </IconButton>

      {/* More Menu */}
      {showMore && (
        <div className="relative" ref={menuRef}>
          <IconButton
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={t('actions.more')}
            aria-expanded={menuOpen}
          >
            <MoreHorizontal className="w-5 h-5" />
          </IconButton>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-lg shadow-lg border border-[#CAC7C7] py-1 z-10"
              >
                <button
                  type="button"
                  onClick={() => handleMenuAction('feedback')}
                  className="w-full px-4 py-2.5 text-left text-sm text-[#2E2A23] hover:bg-[#F4F2F2] flex items-center gap-2 cursor-pointer"
                >
                  <ThumbsDown className="w-4 h-4" />
                  {t('actions.lessLikeThis')}
                </button>
                <button
                  type="button"
                  onClick={() => handleMenuAction('muteAuthor')}
                  className="w-full px-4 py-2.5 text-left text-sm text-[#2E2A23] hover:bg-[#F4F2F2] flex items-center gap-2 cursor-pointer"
                >
                  <VolumeX className="w-4 h-4" />
                  {t('actions.muteAuthor')}
                </button>
                <button
                  type="button"
                  onClick={() => handleMenuAction('muteSource')}
                  className="w-full px-4 py-2.5 text-left text-sm text-[#2E2A23] hover:bg-[#F4F2F2] flex items-center gap-2 cursor-pointer"
                >
                  <VolumeX className="w-4 h-4" />
                  {t('actions.muteSource')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
